import {
	AlertTriangle,
	CheckCircle2,
	ChevronLeft,
	Clock,
	ImageIcon,
	ListChecks,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Bar, BarChart, CartesianGrid, LabelList, XAxis, YAxis } from 'recharts'
import { useVereAgirMetrics } from '@/api/queries/kpi-metrics-queries'
import { ResponsiveFormModal } from '@/components/commons'
import { Button } from '@/components/ui/button'
import {
	type ChartConfig,
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
} from '@/components/ui/chart'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type {
	KpiMetricsParams,
	VereAgirBranchLocationEntry,
	VereAgirBranchResolutionEntry,
} from '@/types'
import { AccordionBarChart } from './shared/accordion-bar-chart'
import { KpiMetricsDashboardLoading } from './shared/kpi-metrics-dashboard-loading'
import { KpiStatCard } from './shared/kpi-stat-card'
import {
	ResponsiveTable,
	type ResponsiveTableColumn,
} from './shared/responsive-table'
import { StatusPill } from './shared/status-pill'
import { WordCloud } from './shared/word-cloud'

interface VereAgirTableRowLike {
	id: string
	crName: string
	date: Date | string
	responsibleName: string
	riskName: string
	description: string
	situation: string
	status: string
	dueDate: Date | string | null
	resolvedAt: Date | string | null
	imagePath?: string | null
	resolutionImagePath?: string | null
}

function buildTableColumns(
	t: (key: string) => string,
	onImageClick: (imagePath: string) => void,
): ResponsiveTableColumn<VereAgirTableRowLike>[] {
	return [
		{
			key: 'crName',
			header: t('kpiMetrics.vereAgir.tableCr'),
			render: (row) => row.crName,
			className: 'max-w-40 truncate',
		},
		{
			key: 'date',
			header: t('kpiMetrics.vereAgir.tableDate'),
			render: (row) =>
				new Date(row.date).toLocaleDateString('pt-BR', { timeZone: 'UTC' }),
			className: 'whitespace-nowrap',
		},
		{
			key: 'responsibleName',
			header: t('kpiMetrics.vereAgir.tableResponsible'),
			render: (row) => row.responsibleName,
			className: 'max-w-40 truncate',
		},
		{
			key: 'riskName',
			header: t('kpiMetrics.vereAgir.tableRisk'),
			render: (row) => row.riskName,
			className: 'max-w-48 truncate',
		},
		{
			key: 'description',
			header: t('kpiMetrics.vereAgir.tableDescription'),
			render: (row) => row.description,
			className: 'max-w-72 truncate',
		},
		{
			key: 'status',
			header: t('kpiMetrics.vereAgir.tableStatus'),
			render: (row) => <StatusPill status={row.status} />,
			hideOnCard: true,
		},
		{
			key: 'imagePath',
			header: t('kpiMetrics.vereAgir.tableImage'),
			render: (row) =>
				row.imagePath ? (
					<Button
						type="button"
						variant="ghost"
						size="icon"
						className="size-8"
						onClick={() => onImageClick(row.imagePath as string)}
					>
						<ImageIcon className="size-4" />
					</Button>
				) : (
					<span className="text-muted-foreground">—</span>
				),
		},
		{
			key: 'resolutionImagePath',
			header: t('kpiMetrics.vereAgir.tableResolutionImage'),
			render: (row) =>
				row.resolutionImagePath ? (
					<Button
						type="button"
						variant="ghost"
						size="icon"
						className="size-8"
						onClick={() => onImageClick(row.resolutionImagePath as string)}
					>
						<ImageIcon className="size-4" />
					</Button>
				) : (
					<span className="text-muted-foreground">—</span>
				),
		},
	]
}

const DIAS_CHART_CONFIG = {
	avgDays: {
		label: 'Média de dias',
		color: 'var(--color-kaefer-sapphire-blue)',
	},
} satisfies ChartConfig

export function VerEAgirDashboard({ params }: { params: KpiMetricsParams }) {
	const { t } = useTranslation()
	const { data: vereAgir, isLoading } = useVereAgirMetrics(params)
	const [previewImage, setPreviewImage] = useState<string | null>(null)
	const [selectedBranch, setSelectedBranch] =
		useState<VereAgirBranchResolutionEntry | null>(null)

	if (isLoading || !vereAgir) return <KpiMetricsDashboardLoading />

	const tableColumns = buildTableColumns(t, setPreviewImage)

	return (
		<div className="flex flex-col gap-4">
			<div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
				<TotalKpiStatCard
					icon={<ListChecks className="size-4" />}
					label={t('kpiMetrics.vereAgir.total')}
					value={vereAgir.kpis.total}
					fixAndContinue={vereAgir.kpis.fixAndContinue}
					stopAndFix={vereAgir.kpis.stopAndFix}
					fixAndContinueLabel={t('kpiMetrics.vereAgir.fixAndContinue')}
					stopAndFixLabel={t('kpiMetrics.vereAgir.stopAndFix')}
				/>
				<KpiStatCard
					icon={<CheckCircle2 className="size-4" />}
					iconClass="bg-kaefer-grass-green/15 text-kaefer-forest-green"
					label={t('kpiMetrics.vereAgir.resolved')}
					value={String(vereAgir.kpis.resolved)}
				/>
				<KpiStatCard
					icon={<Clock className="size-4" />}
					iconClass="bg-kaefer-light-silver/60 text-kaefer-medium-grey"
					label={t('kpiMetrics.vereAgir.open')}
					value={String(vereAgir.kpis.open)}
				/>
				<KpiStatCard
					icon={<AlertTriangle className="size-4" />}
					iconClass={cn(
						vereAgir.kpis.open > 0
							? 'bg-kaefer-red/15 text-kaefer-red'
							: 'bg-kaefer-light-silver/60 text-kaefer-medium-grey',
					)}
					label={t('kpiMetrics.vereAgir.resolutionRate')}
					value={`${Math.round(vereAgir.kpis.resolutionPercentage)}%`}
				/>
			</div>

			<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
				<div className="rounded-lg border border-kaefer-light-silver bg-white p-4">
					<span className="text-sm font-semibold text-kaefer-dark-grey">
						{t('kpiMetrics.vereAgir.byRisk')}
					</span>
					<div className="mt-1 max-h-80 overflow-y-auto pr-1">
						<AccordionBarChart
							items={vereAgir.byRisk.map((kind) => ({
								id: kind.kindId,
								label: kind.kindName,
								value: kind.count,
								children: kind.risks,
							}))}
							getChildId={(risk) => risk.riskId}
							getChildLabel={(risk) => risk.riskName}
							getChildValue={(risk) => risk.count}
							childColorClass="bg-kaefer-red/50"
							emptyState={<EmptyState t={t} />}
							renderRow={(item, maxParent) => {
								const kind = vereAgir.byRisk.find((k) => k.kindId === item.id)
								return (
									<RiskStackedBarRow
										label={item.label}
										risks={kind?.risks ?? []}
										total={item.value}
										max={maxParent}
									/>
								)
							}}
						/>
					</div>
				</div>

				<LocationAccordionCard
					title={t('kpiMetrics.vereAgir.byLocation')}
					data={vereAgir.byLocation}
					t={t}
				/>
				<LocationAccordionCard
					title={t('kpiMetrics.vereAgir.pendingByLocation')}
					data={vereAgir.pendingByLocation}
					t={t}
					variant="pending"
				/>
			</div>

			<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
				<div className="rounded-lg border border-kaefer-light-silver bg-white p-4">
					<div className="flex items-center gap-2">
						{selectedBranch && (
							<Button
								type="button"
								variant="ghost"
								size="icon"
								className="size-6"
								onClick={() => setSelectedBranch(null)}
							>
								<ChevronLeft className="size-4" />
							</Button>
						)}
						<span className="text-sm font-semibold text-kaefer-dark-grey">
							{selectedBranch
								? selectedBranch.branchName
								: t('kpiMetrics.vereAgir.resolutionDays')}
						</span>
					</div>
					{selectedBranch ? (
						<ResolutionDaysChart
							data={selectedBranch.crList}
							categoryKey="crName"
						/>
					) : (
						<ResolutionDaysChart
							data={[...vereAgir.resolutionDays]
								.sort((a, b) => b.avgDays - a.avgDays)
								.slice(0, 20)}
							categoryKey="branchName"
							onBarClick={(branchId) =>
								setSelectedBranch(
									vereAgir.resolutionDays.find(
										(b) => b.branchId === branchId,
									) ?? null,
								)
							}
						/>
					)}
				</div>

				<div className="rounded-lg border border-kaefer-light-silver bg-white p-4">
					<span className="text-sm font-semibold text-kaefer-dark-grey">
						{t('kpiMetrics.vereAgir.words')}
					</span>
					{vereAgir.words.length === 0 ? (
						<EmptyState t={t} />
					) : (
						<div className="mt-3">
							<WordCloud words={vereAgir.words} />
						</div>
					)}
				</div>
			</div>

			<div className="rounded-lg border border-kaefer-light-silver bg-white p-4">
				<span className="text-sm font-semibold text-kaefer-dark-grey">
					{t('kpiMetrics.vereAgir.table')}
				</span>
				{vereAgir.table.length === 0 ? (
					<EmptyState t={t} />
				) : (
					<div className="mt-2 max-h-96 overflow-auto">
						<ResponsiveTable
							columns={tableColumns}
							rows={vereAgir.table}
							rowKey={(row) => row.id}
							cardTitleKey="riskName"
							cardBadge={(row) => <StatusPill status={row.status} />}
						/>
					</div>
				)}
			</div>

			<ResponsiveFormModal
				open={Boolean(previewImage)}
				onOpenChange={(open) => !open && setPreviewImage(null)}
				title={t('kpiMetrics.vereAgir.tableImage')}
				dialogClassName="sm:max-w-2xl"
			>
				{previewImage && (
					<img
						src={previewImage}
						alt={t('kpiMetrics.vereAgir.tableImage')}
						className="max-h-[70vh] w-full rounded-lg object-contain"
					/>
				)}
			</ResponsiveFormModal>
		</div>
	)
}

function EmptyState({ t }: { t: (key: string) => string }) {
	return (
		<p className="py-10 text-center text-sm text-muted-foreground">
			{t('kpiMetrics.charts.noData')}
		</p>
	)
}

function TotalKpiStatCard({
	icon,
	label,
	value,
	fixAndContinue,
	stopAndFix,
	fixAndContinueLabel,
	stopAndFixLabel,
}: {
	icon: React.ReactNode
	label: string
	value: number
	fixAndContinue: number
	stopAndFix: number
	fixAndContinueLabel: string
	stopAndFixLabel: string
}) {
	const total = fixAndContinue + stopAndFix
	const fixPct = total > 0 ? Math.round((fixAndContinue / total) * 100) : 0
	const stopPct = total > 0 ? 100 - fixPct : 0

	return (
		<div className="rounded-lg border border-kaefer-light-silver bg-white p-4">
			<div className="flex size-8 items-center justify-center rounded-md bg-kaefer-sapphire-blue/15 text-kaefer-sapphire-blue">
				{icon}
			</div>
			<p className="mt-2 text-2xl font-bold text-kaefer-dark-grey">{value}</p>
			<p className="text-xs font-medium text-kaefer-dark-grey">{label}</p>

			<div className="mt-2 flex flex-col gap-1 border-kaefer-light-silver border-t pt-2">
				<div className="flex items-center justify-between gap-2">
					<span className="text-[11px] text-muted-foreground">
						{fixAndContinueLabel}
					</span>
					<span className="text-xs font-bold text-kaefer-forest-green tabular-nums">
						{fixAndContinue} ({fixPct}%)
					</span>
				</div>
				<div className="flex items-center justify-between gap-2">
					<span className="text-[11px] text-muted-foreground">
						{stopAndFixLabel}
					</span>
					<span className="text-xs font-bold text-kaefer-red tabular-nums">
						{stopAndFix} ({stopPct}%)
					</span>
				</div>
			</div>
		</div>
	)
}

const RISK_STACK_COLORS = [
	'bg-kaefer-sapphire-blue',
	'bg-kaefer-red',
	'bg-kaefer-honey-yellow',
	'bg-kaefer-grass-green',
	'bg-kaefer-violet',
	'bg-kaefer-astral-blue',
	'bg-kaefer-pumpkin-orange',
	'bg-kaefer-forest-green',
	'bg-kaefer-fuchsia-pink',
	'bg-kaefer-medium-grey',
]

function RiskStackedBarRow({
	label,
	risks,
	total,
	max,
}: {
	label: string
	risks: { riskId: string; riskName: string; count: number }[]
	total: number
	max: number
}) {
	const widthPct = Math.max((total / max) * 100, total > 0 ? 2 : 0)

	return (
		<div className="grid grid-cols-[1fr_2fr_auto] items-center gap-2.5">
			<span className="truncate text-xs text-kaefer-dark-grey" title={label}>
				{label}
			</span>
			<Tooltip>
				<TooltipTrigger asChild>
					<div className="h-3.5 overflow-hidden bg-kaefer-light-silver/60">
						<div className="flex h-full" style={{ width: `${widthPct}%` }}>
							{risks.map((risk, index) => (
								<div
									key={risk.riskId}
									className={cn(
										'h-full',
										RISK_STACK_COLORS[index % RISK_STACK_COLORS.length],
									)}
									style={{ width: `${(risk.count / total) * 100}%` }}
								/>
							))}
						</div>
					</div>
				</TooltipTrigger>
				<TooltipContent side="top" align="center" className="max-w-64">
					<div className="flex flex-col gap-1">
						{risks.map((risk, index) => (
							<div
								key={risk.riskId}
								className="grid grid-cols-[auto_1fr_auto] items-center gap-1.5"
							>
								<span
									className={cn(
										'size-2 shrink-0 rounded-[2px]',
										RISK_STACK_COLORS[index % RISK_STACK_COLORS.length],
									)}
								/>
								<span className="truncate text-left" title={risk.riskName}>
									{risk.riskName}
								</span>
								<span className="text-muted-foreground tabular-nums">
									{risk.count} ({Math.round((risk.count / total) * 100)}%)
								</span>
							</div>
						))}
					</div>
				</TooltipContent>
			</Tooltip>
			<span className="text-right text-xs font-semibold text-muted-foreground tabular-nums">
				{total}
			</span>
		</div>
	)
}

function LocationAccordionCard({
	title,
	data,
	t,
	variant = 'default',
}: {
	title: string
	data: VereAgirBranchLocationEntry[]
	t: (key: string) => string
	variant?: 'default' | 'pending'
}) {
	const BarRow = variant === 'pending' ? PendingLocationBarRow : LocationBarRow

	return (
		<div className="rounded-lg border border-kaefer-light-silver bg-white p-4">
			<span className="text-sm font-semibold text-kaefer-dark-grey">
				{title}
			</span>
			<div className="mt-1 max-h-80 overflow-y-auto pr-1">
				<AccordionBarChart
					items={data.map((branch) => ({
						id: branch.branchId,
						label: branch.branchName,
						value: branch.fixAndContinue + branch.stopAndFix,
						children: branch.crList,
					}))}
					getChildId={(cr) => cr.crId}
					getChildLabel={(cr) => cr.crName}
					getChildValue={(cr) => cr.fixAndContinue + cr.stopAndFix}
					emptyState={<EmptyState t={t} />}
					renderRow={(item, maxParent) => (
						<BarRow
							label={item.label}
							fixAndContinue={
								data.find((b) => b.branchId === item.id)?.fixAndContinue ?? 0
							}
							stopAndFix={
								data.find((b) => b.branchId === item.id)?.stopAndFix ?? 0
							}
							max={maxParent}
							fixAndContinueLabel={t('kpiMetrics.vereAgir.fixAndContinue')}
							stopAndFixLabel={t('kpiMetrics.vereAgir.stopAndFix')}
						/>
					)}
					renderChildren={(item) => {
						const maxChild = Math.max(
							1,
							...(item.children ?? []).map(
								(cr) => cr.fixAndContinue + cr.stopAndFix,
							),
						)
						return (
							<div className="flex flex-col gap-2">
								{(item.children ?? []).map((cr) => (
									<BarRow
										key={cr.crId}
										label={cr.crName}
										fixAndContinue={cr.fixAndContinue}
										stopAndFix={cr.stopAndFix}
										max={maxChild}
										fixAndContinueLabel={t(
											'kpiMetrics.vereAgir.fixAndContinue',
										)}
										stopAndFixLabel={t('kpiMetrics.vereAgir.stopAndFix')}
										muted
									/>
								))}
							</div>
						)
					}}
				/>
			</div>
			<div className="mt-3 flex gap-4 text-[11px] text-muted-foreground">
				{variant === 'pending' ? (
					<span className="flex items-center gap-1.5">
						<span className="size-2.5 rounded-[2px] bg-kaefer-red" />
						{t('kpiMetrics.vereAgir.pendingByLocation')}
					</span>
				) : (
					<>
						<span className="flex items-center gap-1.5">
							<span className="size-2.5 rounded-[2px] bg-kaefer-grass-green" />
							{t('kpiMetrics.vereAgir.fixAndContinue')}
						</span>
						<span className="flex items-center gap-1.5">
							<span className="size-2.5 rounded-[2px] bg-kaefer-red" />
							{t('kpiMetrics.vereAgir.stopAndFix')}
						</span>
					</>
				)}
			</div>
		</div>
	)
}

function LocationBarRow({
	label,
	fixAndContinue,
	stopAndFix,
	max,
	fixAndContinueLabel,
	stopAndFixLabel,
	muted,
}: {
	label: string
	fixAndContinue: number
	stopAndFix: number
	max: number
	fixAndContinueLabel: string
	stopAndFixLabel: string
	muted?: boolean
}) {
	const total = fixAndContinue + stopAndFix
	const widthPct = Math.max((total / max) * 100, total > 0 ? 2 : 0)
	const fixPct = total > 0 ? (fixAndContinue / total) * 100 : 0
	const stopPct = total > 0 ? (stopAndFix / total) * 100 : 0

	return (
		<div className="grid grid-cols-[1fr_2fr_auto] items-center gap-2.5">
			<span
				className={cn(
					'truncate text-xs',
					muted ? 'text-muted-foreground' : 'text-kaefer-dark-grey',
				)}
				title={label}
			>
				{label}
			</span>
			<Tooltip>
				<TooltipTrigger asChild>
					<div className="h-3.5 overflow-hidden bg-kaefer-light-silver/60">
						<div className="flex h-full" style={{ width: `${widthPct}%` }}>
							<div
								className="h-full bg-kaefer-grass-green"
								style={{ width: `${fixPct}%` }}
							/>
							<div
								className="h-full bg-kaefer-red"
								style={{ width: `${stopPct}%` }}
							/>
						</div>
					</div>
				</TooltipTrigger>
				<TooltipContent side="top" align="center" className="max-w-64">
					<div className="flex flex-col gap-1">
						<div className="grid grid-cols-[auto_1fr_auto] items-center gap-1.5">
							<span className="size-2 shrink-0 rounded-[2px] bg-kaefer-grass-green" />
							<span className="truncate text-left">{fixAndContinueLabel}</span>
							<span className="text-muted-foreground tabular-nums">
								{fixAndContinue} ({Math.round(fixPct)}%)
							</span>
						</div>
						<div className="grid grid-cols-[auto_1fr_auto] items-center gap-1.5">
							<span className="size-2 shrink-0 rounded-[2px] bg-kaefer-red" />
							<span className="truncate text-left">{stopAndFixLabel}</span>
							<span className="text-muted-foreground tabular-nums">
								{stopAndFix} ({Math.round(stopPct)}%)
							</span>
						</div>
					</div>
				</TooltipContent>
			</Tooltip>
			<span className="text-right text-xs font-semibold text-muted-foreground tabular-nums">
				{total}
			</span>
		</div>
	)
}

function PendingLocationBarRow({
	label,
	fixAndContinue,
	stopAndFix,
	max,
	fixAndContinueLabel,
	stopAndFixLabel,
	muted,
}: {
	label: string
	fixAndContinue: number
	stopAndFix: number
	max: number
	fixAndContinueLabel: string
	stopAndFixLabel: string
	muted?: boolean
}) {
	const total = fixAndContinue + stopAndFix
	const widthPct = Math.max((total / max) * 100, total > 0 ? 2 : 0)
	const fixPct = total > 0 ? (fixAndContinue / total) * 100 : 0
	const stopPct = total > 0 ? (stopAndFix / total) * 100 : 0

	return (
		<div className="grid grid-cols-[1fr_2fr_auto] items-center gap-2.5">
			<span
				className={cn(
					'truncate text-xs',
					muted ? 'text-muted-foreground' : 'text-kaefer-dark-grey',
				)}
				title={label}
			>
				{label}
			</span>
			<Tooltip>
				<TooltipTrigger asChild>
					<div className="h-3.5 overflow-hidden bg-kaefer-light-silver/60">
						<div
							className="h-full bg-kaefer-red"
							style={{ width: `${widthPct}%` }}
						/>
					</div>
				</TooltipTrigger>
				<TooltipContent side="top" align="center" className="max-w-64">
					<div className="flex flex-col gap-1">
						<div className="grid grid-cols-[auto_1fr_auto] items-center gap-1.5">
							<span className="size-2 shrink-0 rounded-[2px] bg-kaefer-grass-green" />
							<span className="truncate text-left">{fixAndContinueLabel}</span>
							<span className="text-muted-foreground tabular-nums">
								{fixAndContinue} ({Math.round(fixPct)}%)
							</span>
						</div>
						<div className="grid grid-cols-[auto_1fr_auto] items-center gap-1.5">
							<span className="size-2 shrink-0 rounded-[2px] bg-kaefer-red" />
							<span className="truncate text-left">{stopAndFixLabel}</span>
							<span className="text-muted-foreground tabular-nums">
								{stopAndFix} ({Math.round(stopPct)}%)
							</span>
						</div>
					</div>
				</TooltipContent>
			</Tooltip>
			<span className="text-right text-xs font-semibold text-muted-foreground tabular-nums">
				{total}
			</span>
		</div>
	)
}

function ResolutionDaysChart<
	TRow extends { avgDays: number; resolvedCount: number; branchId?: string },
>({
	data,
	categoryKey,
	onBarClick,
}: {
	data: TRow[]
	categoryKey: keyof TRow & string
	onBarClick?: (branchId: string) => void
}) {
	const { t } = useTranslation()

	if (data.length === 0) return <EmptyState t={t} />

	return (
		<ChartContainer
			config={DIAS_CHART_CONFIG}
			className="mx-auto mt-2 max-h-80 w-full"
		>
			<BarChart data={data} layout="vertical" margin={{ left: 8, right: 32 }}>
				<CartesianGrid horizontal={false} />
				<XAxis type="number" />
				<YAxis
					type="category"
					dataKey={categoryKey as string}
					width={140}
					tick={{ fontSize: 11 }}
				/>
				<ChartTooltip
					content={
						<ChartTooltipContent
							hideLabel
							formatter={(value, _name, item) => {
								const payload = item.payload as TRow
								return (
									<div className="flex w-full min-w-40 flex-col gap-0.5">
										<span className="font-medium text-kaefer-dark-grey">
											{String(payload[categoryKey])}
										</span>
										<span className="text-muted-foreground">
											{Number(value).toFixed(2)} dias · {payload.resolvedCount}{' '}
											resolvidos
										</span>
									</div>
								)
							}}
						/>
					}
				/>
				<Bar
					dataKey="avgDays"
					fill="var(--color-avgDays)"
					radius={4}
					barSize={20}
					cursor={onBarClick ? 'pointer' : undefined}
					onClick={
						onBarClick
							? (entry) => {
									const row = entry as unknown as TRow
									if (row.branchId) onBarClick(row.branchId)
								}
							: undefined
					}
				>
					<LabelList
						dataKey="avgDays"
						position="right"
						className="fill-kaefer-dark-grey"
						fontSize={11}
						formatter={(value) =>
							typeof value === 'number' ? value.toFixed(1) : ''
						}
					/>
				</Bar>
			</BarChart>
		</ChartContainer>
	)
}
